import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  listUsers,
  getRoles,
  createUser,
  updateUser,
  deleteUser,
  activateUser,
  deactivateUser,
  resetUserMfa,
  getAuditLogs,
  ROLES,
  ROLE_LABELS,
  isSuperAdmin,
  isCompanyAdmin,
  getEffectiveRole,
} from '../services/users';
import { getManufacturers } from '../services/manufacturers';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  Button,
  TableContainer,
  StyledTable,
  Th,
  Td,
  StatusChip,
  IconButton,
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  HelperText,
  Select,
  ErrorAlert,
  EmptyState,
  LoadingText,
  MutedText,
  InlineCode,
  Checkbox,
  H2Title,
} from '../components/styled';
import { IconX, IconCheck, IconTrash, IconUserOff, IconUserCheck, IconShieldOff } from '@tabler/icons-react';
import { SegmentedTabs } from '../components/SegmentedTabs';
import { MIN_PASSWORD_LENGTH } from '../constants/auth';

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const ActionCell = styled(Td)`
  white-space: nowrap;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const InactiveRow = styled.tr`
  opacity: 0.5;
`;

// Compact controls used when a row is switched into inline-edit mode — kept
// narrow so the table doesn't jump width while editing.
const InlineInput = styled(Input)`
  min-width: 140px;
`;

const InlineSelect = styled(Select)`
  min-width: 120px;
`;

// Full-width error strip shown directly beneath the row being edited, since
// inline editing has no modal to host the ErrorAlert.
const RowError = styled.td`
  padding: ${({ theme }) => theme.spacing(0, 3, 2)};
  color: ${({ theme }) => theme.color.red};
  font-size: ${({ theme }) => theme.font.size.sm};
`;

// Selection toolbar — appears above the table once one or more rows are
// checked, hosting the bulk Deactivate / Delete actions.
const BulkBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2, 3)};
  background: ${({ theme }) => theme.background.secondary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.md};
`;

const BulkCount = styled.span`
  margin-right: auto;
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.font.color.primary};
`;

// Narrow leading column that holds the row-selection checkbox.
const SelectCell = styled(Td)`
  width: 36px;
  text-align: center;
`;

const SelectTh = styled(Th)`
  width: 36px;
`;

function Users() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('users');
  const [showModal, setShowModal] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const isUserSuperAdmin = isSuperAdmin(currentUser);
  const isUserCompanyAdmin = isCompanyAdmin(currentUser);

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => listUsers(),
  });

  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  });

  const { data: auditData, isLoading: auditLoading } = useQuery({
    queryKey: ['auditLogs'],
    queryFn: () => getAuditLogs({ limit: 50 }),
    enabled: isUserCompanyAdmin,
  });

  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await api.get('/companies');
      return response.data;
    },
    enabled: isUserSuperAdmin,
  });

  // Manufacturer admin accounts belong to a manufacturer instead of a company.
  const { data: manufacturers = [] } = useQuery({
    queryKey: ['manufacturers'],
    queryFn: () => getManufacturers(),
    enabled: isUserSuperAdmin,
  });

  const createUserMutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setShowModal(null);
      setFormData({});
      setError(null);
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to create user'),
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }) => updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setEditingUserId(null);
      setFormData({});
      setError(null);
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to update user'),
  });

  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => queryClient.invalidateQueries(['users']),
    onError: (err) => setError(err.response?.data?.error || 'Failed to delete user'),
  });

  const activateMutation = useMutation({
    mutationFn: activateUser,
    onSuccess: () => queryClient.invalidateQueries(['users']),
  });

  const deactivateMutation = useMutation({
    mutationFn: deactivateUser,
    onSuccess: () => queryClient.invalidateQueries(['users']),
  });

  const resetMfaMutation = useMutation({
    mutationFn: resetUserMfa,
    onSuccess: () => queryClient.invalidateQueries(['users']),
    onError: (err) => setError(err.response?.data?.error || 'Failed to reset two-factor authentication'),
  });

  const users = usersData?.users || [];
  const auditLogs = auditData?.logs || [];
  const availableRoles = rolesData?.roles || [];
  const companies = companiesData?.companies || [];

  // Users the current admin is allowed to act on in bulk (everyone but
  // themselves). Non-admins get an empty set, so no checkboxes appear.
  const manageableUsers = isUserCompanyAdmin
    ? users.filter((u) => u.id !== currentUser.id)
    : [];
  const allSelected =
    manageableUsers.length > 0 && selectedIds.length === manageableUsers.length;

  const handleOpenCreate = () => {
    setFormData({
      email: '',
      name: '',
      password: '',
      role: ROLES.COMPANY_ADMIN,
      companyId: currentUser?.company_id || '',
      manufacturerId: '',
    });
    setShowModal('create');
    setError(null);
  };

  const handleStartEdit = (user) => {
    setEditingUserId(user.id);
    setFormData({
      name: user.name,
      role_new: getEffectiveRole(user),
      is_active: user.is_active,
      company_id: user.company_id || '',
      manufacturer_id: user.manufacturer_id || '',
    });
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setFormData({});
    setError(null);
  };

  const handleSaveEdit = () => {
    updateUserMutation.mutate({ id: editingUserId, data: formData });
  };

  const handleSubmitCreate = (e) => {
    e.preventDefault();
    createUserMutation.mutate(formData);
  };

  const toggleSelect = (userId) => {
    setSelectedIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : manageableUsers.map((u) => u.id));
  };

  const handleBulkActivate = () => {
    if (selectedIds.length === 0) return;
    selectedIds.forEach((id) => activateMutation.mutate(id));
    setSelectedIds([]);
  };

  const handleBulkDeactivate = () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Deactivate ${selectedIds.length} user(s)?`)) return;
    selectedIds.forEach((id) => deactivateMutation.mutate(id));
    setSelectedIds([]);
  };

  const handleBulkResetMfa = () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Reset two-factor authentication for ${selectedIds.length} user(s)? They will set it up again at their next sign-in.`)) return;
    selectedIds.forEach((id) => resetMfaMutation.mutate(id));
    setSelectedIds([]);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} user(s)? This cannot be undone.`)) return;
    selectedIds.forEach((id) => deleteUserMutation.mutate(id));
    setSelectedIds([]);
  };

  const roleBadgeVariant = (role) => {
    switch (role) {
      case ROLES.SUPER_ADMIN:  return 'purple';
      case ROLES.COMPANY_ADMIN: return 'blue';
      case ROLES.MANUFACTURER_ADMIN: return 'warning';
      default:                  return 'gray';
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>User Management</PageTitle>
        <HeaderActions>
          {isUserCompanyAdmin && (
            <Button size="sm" variant="secondary" onClick={handleOpenCreate}>
              Create User
            </Button>
          )}
        </HeaderActions>
      </PageHeader>

      <SegmentedTabs
        tabs={[
          { id: 'users', label: 'Users' },
          ...(isUserCompanyAdmin ? [{ id: 'audit', label: 'Audit Logs' }] : []),
        ]}
        activeId={activeTab}
        onChange={setActiveTab}
      />

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {selectedIds.length > 0 && (
            <BulkBar>
              <BulkCount>{selectedIds.length} selected</BulkCount>
              <Button size="sm" variant="secondary" onClick={handleBulkActivate}>
                <IconUserCheck size={14} stroke={2} />Activate
              </Button>
              <Button size="sm" variant="secondary" onClick={handleBulkDeactivate}>
                <IconUserOff size={14} stroke={2} />Deactivate
              </Button>
              <Button size="sm" variant="secondary" onClick={handleBulkResetMfa} title="Clear their authenticator so they can enroll a new one">
                <IconShieldOff size={14} stroke={2} />Reset 2FA
              </Button>
              {isUserSuperAdmin && (
                <Button size="sm" variant="danger" onClick={handleBulkDelete}>
                  <IconTrash size={14} stroke={2} />Delete
                </Button>
              )}
              <IconButton type="button" title="Clear selection" aria-label="Clear selection" onClick={() => setSelectedIds([])}>
                <IconX size={16} stroke={2} />
              </IconButton>
            </BulkBar>
          )}
          {usersLoading ? (
            <LoadingText>Loading users...</LoadingText>
          ) : (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <SelectTh>
                      {manageableUsers.length > 0 && (
                        <Checkbox checked={allSelected} onChange={toggleSelectAll} />
                      )}
                    </SelectTh>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>Role</Th>
                    <Th>Company</Th>
                    <Th>Status</Th>
                    <Th>Last Login</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const isEditing = editingUserId === user.id;
                    // Keep the row full-opacity while editing even if the user
                    // is inactive, so the inline inputs stay readable.
                    const Row = (!user.is_active && !isEditing) ? InactiveRow : 'tr';
                    const canManage = user.id !== currentUser.id && isUserCompanyAdmin;
                    const effectiveRole = getEffectiveRole(user);
                    return (
                      <Fragment key={user.id}>
                        <Row>
                          <SelectCell>
                            {canManage && (
                              <Checkbox
                                checked={selectedIds.includes(user.id)}
                                onChange={() => toggleSelect(user.id)}
                              />
                            )}
                          </SelectCell>
                          <Td>
                            {isEditing ? (
                              <InlineInput
                                autoFocus
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                disabled={updateUserMutation.isPending}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveEdit();
                                  if (e.key === 'Escape') handleCancelEdit();
                                }}
                              />
                            ) : (
                              user.name
                            )}
                          </Td>
                          <Td>{user.email}</Td>
                          <Td>
                            {isEditing ? (
                              <InlineSelect
                                value={formData.role_new}
                                onChange={(e) => setFormData({ ...formData, role_new: e.target.value })}
                                disabled={updateUserMutation.isPending}
                              >
                                {availableRoles.map((role) => (
                                  <option key={role.value} value={role.value}>
                                    {role.label}
                                  </option>
                                ))}
                              </InlineSelect>
                            ) : (
                              <StatusChip variant={roleBadgeVariant(effectiveRole)}>
                                {ROLE_LABELS[effectiveRole] || effectiveRole}
                              </StatusChip>
                            )}
                          </Td>
                          <Td>
                            {isEditing && isUserSuperAdmin && formData.role_new === ROLES.MANUFACTURER_ADMIN ? (
                              <InlineSelect
                                value={formData.manufacturer_id}
                                onChange={(e) => setFormData({ ...formData, manufacturer_id: e.target.value })}
                                disabled={updateUserMutation.isPending}
                              >
                                <option value="">Select a manufacturer</option>
                                {manufacturers.map((m) => (
                                  <option key={m.id} value={m.id}>
                                    {m.name}
                                  </option>
                                ))}
                              </InlineSelect>
                            ) : isEditing && isUserSuperAdmin && formData.role_new !== ROLES.SUPER_ADMIN ? (
                              <InlineSelect
                                value={formData.company_id}
                                onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                                disabled={updateUserMutation.isPending}
                              >
                                <option value="">Select a company</option>
                                {companies.map((company) => (
                                  <option key={company.id} value={company.id}>
                                    {company.name}
                                  </option>
                                ))}
                              </InlineSelect>
                            ) : (
                              user.company_name || user.manufacturer_name || '-'
                            )}
                          </Td>
                          <Td>
                            <StatusChip variant={user.is_active ? 'green' : 'red'}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </StatusChip>
                          </Td>
                          <Td>
                            {user.last_login_at
                              ? new Date(user.last_login_at).toLocaleDateString()
                              : 'Never'}
                          </Td>
                          <ActionCell>
                            {isEditing ? (
                              <ActionGroup>
                                <IconButton
                                  type="button"
                                  title="Save"
                                  aria-label="Save changes"
                                  onClick={handleSaveEdit}
                                  disabled={updateUserMutation.isPending}
                                >
                                  <IconCheck size={16} stroke={2} />
                                </IconButton>
                                <IconButton
                                  type="button"
                                  title="Cancel"
                                  aria-label="Cancel edit"
                                  onClick={handleCancelEdit}
                                  disabled={updateUserMutation.isPending}
                                >
                                  <IconX size={16} stroke={2} />
                                </IconButton>
                              </ActionGroup>
                            ) : canManage ? (
                              <Button size="sm" variant="secondary" onClick={() => handleStartEdit(user)}>
                                Edit
                              </Button>
                            ) : user.id === currentUser.id ? (
                              <MutedText>You</MutedText>
                            ) : null}
                          </ActionCell>
                        </Row>
                        {isEditing && error && (
                          <tr>
                            <RowError colSpan={8}>{error}</RowError>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}
        </>
      )}

      {/* Audit Logs Tab */}
      {activeTab === 'audit' && isUserCompanyAdmin && (
        <>
          {auditLoading ? (
            <LoadingText>Loading audit logs...</LoadingText>
          ) : auditLogs.length === 0 ? (
            <EmptyState>
              <p>No audit logs</p>
            </EmptyState>
          ) : (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Time</Th>
                    <Th>User</Th>
                    <Th>Action</Th>
                    <Th>Resource</Th>
                    <Th>IP Address</Th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => (
                    <tr key={log.id}>
                      <Td>{new Date(log.created_at).toLocaleString()}</Td>
                      <Td>{log.user_name || log.user_email}</Td>
                      <Td><InlineCode>{log.action}</InlineCode></Td>
                      <Td>
                        {log.resource_type}
                        {log.resource_id && (
                          <MutedText> ({log.resource_id.slice(0, 8)}...)</MutedText>
                        )}
                      </Td>
                      <Td>{log.ip_address}</Td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}
        </>
      )}

      {/* Create User Modal */}
      {showModal === 'create' && (
        <ModalOverlay onClick={() => setShowModal(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Create User</H2Title>
              <ModalCloseButton onClick={() => setShowModal(null)} aria-label="Close">
                <IconX size={18} stroke={2} />
              </ModalCloseButton>
            </ModalHeader>
            <form onSubmit={handleSubmitCreate}>
              <ModalBody>
                {error && <ErrorAlert>{error}</ErrorAlert>}
                <FormGroup>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Name</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Role</Label>
                  <Select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    {availableRoles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
                {isUserSuperAdmin && formData.role === ROLES.MANUFACTURER_ADMIN && (
                  <FormGroup>
                    <Label>Manufacturer</Label>
                    <Select
                      value={formData.manufacturerId}
                      onChange={(e) => setFormData({ ...formData, manufacturerId: e.target.value })}
                      required
                    >
                      <option value="">Select a manufacturer</option>
                      {manufacturers.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </Select>
                    <HelperText>
                      Seller account: sees only their boards and pricing, nothing company-scoped.
                    </HelperText>
                  </FormGroup>
                )}
                {isUserSuperAdmin && formData.role !== ROLES.SUPER_ADMIN && formData.role !== ROLES.MANUFACTURER_ADMIN && (
                  <FormGroup>
                    <Label>Company</Label>
                    <Select
                      value={formData.companyId}
                      onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                      required
                    >
                      <option value="">Select a company</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" type="button" onClick={() => setShowModal(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createUserMutation.isPending}>
                  {createUserMutation.isPending ? 'Creating...' : 'Create User'}
                </Button>
              </ModalFooter>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}

    </PageContainer>
  );
}

export default Users;
